import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import MobileActionBar from '../components/MobileActionBar';
import {
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  ArrowRight,
  Key,
  Paintbrush,
  Hammer,
  Droplets,
  Layers,
  AlertCircle,
  Clock,
  Shield
} from 'lucide-react';
import { companyInfo } from '../data/mock';

const companyPhone = '07300 405045';

const EndOfTenancy = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    property_address: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          properties_managed: formData.property_address,
          job_description: `[End of Tenancy Repairs] ${formData.message}`,
          trade_type: 'general',
          source_website: 'expert-propertymaintenance.co.uk',
          source_page: '/end-of-tenancy'
        }),
      });
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', property_address: '', message: '' });
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const commonRepairs = [
    {
      icon: Layers,
      title: 'Wall Repairs',
      description: 'Holes from fixtures, picture hooks, TV mounts. Plaster repairs, crack filling and wall preparation.'
    },
    {
      icon: Paintbrush,
      title: 'Decorating',
      description: 'Repainting marked walls, touching up scuffs and scratches, full room redecorates where needed.'
    },
    {
      icon: Hammer,
      title: 'Door Repairs',
      description: 'Damaged doors, broken handles, hinges, latches. Door adjustments, repairs or replacements.'
    },
    {
      icon: Hammer,
      title: 'Joinery Repairs',
      description: 'Skirting boards, architraves, window boards, shelving repairs and general woodwork.'
    },
    {
      icon: Droplets,
      title: 'Sealant & Grouting',
      description: 'Bathroom and kitchen re-sealing. Grout repairs and tile cleaning. Preventing damp issues.'
    },
    {
      icon: Droplets,
      title: 'Minor Plumbing',
      description: 'Leaking taps, toilet issues, blocked drains, shower problems and general plumbing repairs.'
    },
    {
      icon: AlertCircle,
      title: 'Minor Electrical',
      description: 'Broken sockets, light switches, missing bulbs, extractor fan issues — where appropriate.'
    },
    {
      icon: Layers,
      title: 'Flooring Repairs',
      description: 'Damaged laminate, loose floorboards, carpet threshold strips, vinyl repairs.'
    }
  ];

  const importantNote = {
    title: 'Repairs & Maintenance — Not Cleaning',
    description: 'Expert Property Maintenance provides repair and maintenance services for end-of-tenancy turnarounds. We are not an end-of-tenancy cleaning company. Our focus is on fixing damage, carrying out repairs and preparing properties for new tenants through skilled trade work.'
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>End of Tenancy Repairs | Property Turnaround | Expert Property Maintenance</title>
        <meta name="description" content="End of tenancy repairs for landlords and letting agents in Manchester. Wall repairs, decorating, door fixes, plumbing. Quick turnaround for reletting. Call 07300 405045." />
        <link rel="canonical" href="https://expert-propertymaintenance.co.uk/end-of-tenancy" />
      </Helmet>
      <Header variant="default" companyPhone={companyPhone} />
      

      {/* Hero */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-20 bg-gradient-to-b from-brand-navy to-brand-navy/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Key className="w-4 h-4" />
              For Landlords & Letting Agents
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              End of Tenancy{' '}
              <span className="text-brand-yellow">Repairs & Property Refresh</span>
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              When tenants move out, there is often a list of repairs and maintenance needed 
              before the property is ready for new tenants. Expert Property Maintenance helps 
              landlords and letting agents across Greater Manchester prepare properties quickly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
                <a href="#contact">
                  Request a Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy" asChild>
                <a href={`tel:${companyPhone}`}>
                  <Phone className="w-5 h-5 mr-2" />
                  {companyPhone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-8 bg-brand-grey border-y border-brand-yellow/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-brand-navy" />
            </div>
            <div>
              <h3 className="font-bold text-brand-navy mb-1">{importantNote.title}</h3>
              <p className="text-brand-slate">{importantNote.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Common Repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-brand-navy mb-4">
              Common End of Tenancy Repairs
            </h2>
            <p className="text-lg text-brand-slate max-w-2xl mx-auto">
              From small fixes to complete property refreshes — we handle the repairs that 
              get your property ready for new tenants.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {commonRepairs.map((repair) => (
              <Card key={repair.title} className="border-slate-200 hover:border-brand-yellow hover:shadow-lg transition-all group">
                <CardContent className="p-5">
                  <div className="w-10 h-10 bg-brand-grey rounded-lg flex items-center justify-center mb-3 group-hover:bg-brand-yellow transition-colors">
                    <repair.icon className="w-5 h-5 text-brand-yellow group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-base font-bold text-brand-navy mb-1">{repair.title}</h3>
                  <p className="text-brand-slate text-sm">{repair.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Quick Turnaround Matters */}
      <section className="py-16 bg-brand-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-brand-navy mb-6">
                Minimise Void Periods
              </h2>
              <p className="text-lg text-brand-slate mb-6">
                Every day a property sits empty is lost rental income. Expert Property Maintenance 
                works efficiently to complete end-of-tenancy repairs quickly, so you can start 
                showing the property to new tenants sooner.
              </p>
              <div className="space-y-4">
                {[
                  'Fast response to repair requests',
                  'Multi-trade capability — one visit for multiple jobs',
                  'Flexible scheduling around viewings',
                  'Clear communication throughout',
                  'Quality workmanship guaranteed'
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0" />
                    <span className="text-brand-slate">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
              <h3 className="text-xl font-bold text-brand-navy mb-6">How It Works</h3>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-brand-yellow rounded-full flex items-center justify-center flex-shrink-0 font-bold text-brand-navy">1</div>
                  <div>
                    <h4 className="font-semibold text-brand-navy">Send Us the List</h4>
                    <p className="text-sm text-brand-slate">Share your check-out report, photos and repair list.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-brand-yellow rounded-full flex items-center justify-center flex-shrink-0 font-bold text-brand-navy">2</div>
                  <div>
                    <h4 className="font-semibold text-brand-navy">We Quote the Work</h4>
                    <p className="text-sm text-brand-slate">Clear pricing for each repair or as a package.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-brand-yellow rounded-full flex items-center justify-center flex-shrink-0 font-bold text-brand-navy">3</div>
                  <div>
                    <h4 className="font-semibold text-brand-navy">Repairs Completed</h4>
                    <p className="text-sm text-brand-slate">Our tradespeople complete the work to a high standard.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-brand-yellow rounded-full flex items-center justify-center flex-shrink-0 font-bold text-brand-navy">4</div>
                  <div>
                    <h4 className="font-semibold text-brand-navy">Property Ready</h4>
                    <p className="text-sm text-brand-slate">Property prepared for viewings and new tenants.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Property Ready for New Tenants
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Send us your check-out report or repair list and we will get back to you with 
            a quote within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
              <a href="#contact">
                Request a Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy" asChild>
              <a href={`tel:${companyPhone}`}>
                <Phone className="w-5 h-5 mr-2" />
                {companyPhone}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-16 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-6">Request End of Tenancy Repairs</h2>
              <p className="text-slate-300 text-lg mb-8">
                Tell us about the property and what needs doing. Include your check-out report 
                or repair list if available.
              </p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Call us</div>
                    <a href={`tel:${companyPhone}`} className="text-white font-bold text-lg hover:text-brand-yellow">
                      {companyPhone}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Email us</div>
                    <a href={`mailto:${companyInfo.email}`} className="text-white font-bold hover:text-brand-yellow">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-slate-400 text-sm">Service area</div>
                    <div className="text-white font-bold">Greater Manchester & Cheshire</div>
                  </div>
                </div>
              </div>
              <div className="mt-8 flex gap-6">
                <div className="flex items-center gap-2 text-slate-300">
                  <Shield className="w-5 h-5 text-brand-yellow" />
                  <span className="text-sm">Fully insured</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <Clock className="w-5 h-5 text-brand-yellow" />
                  <span className="text-sm">Fast turnaround</span>
                </div>
              </div>
            </div>

            <Card className="bg-white border-0">
              <CardContent className="p-6 sm:p-8">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-brand-navy mb-2">Enquiry Received</h3>
                    <p className="text-brand-slate">We will be in touch within 24 hours.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h3 className="text-xl font-bold text-brand-navy mb-4">Get a Repair Quote</h3>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Your Name *</label>
                      <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Smith" className="h-12" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-brand-slate mb-1.5">Email *</label>
                        <Input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@company.com" className="h-12" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-brand-slate mb-1.5">Phone *</label>
                        <Input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="07xxx xxxxxx" className="h-12" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Property Address / Area</label>
                      <Input value={formData.property_address} onChange={(e) => setFormData({...formData, property_address: e.target.value})} placeholder="e.g., Flat 2, Stockport" className="h-12" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">What repairs are needed? *</label>
                      <Textarea required value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="List the repairs from your check-out report, or describe what needs doing..." rows={4} />
                    </div>
                    <Button type="submit" className="w-full bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold h-12 text-lg" disabled={isSubmitting}>
                      {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p>© {new Date().getFullYear()} Expert Property Maintenance - End of Tenancy Repairs Manchester</p>
            <div className="flex gap-6">
              <a href="/" className="hover:text-brand-yellow">Home</a>
              <a href="/landlord-services" className="hover:text-brand-yellow">Landlord Services</a>
              <a href="/property-refurbishment" className="hover:text-brand-yellow">Refurbishment</a>
              <a href="/#contact" className="hover:text-brand-yellow">Contact</a>
            </div>
          </div>
        </div>
      </footer>
      <MobileActionBar phone={companyPhone} />
    </div>
  );
};

export default EndOfTenancy;
